import React, { useCallback, useEffect, useRef, useState } from 'react';
import ChatMessage from './ChatMessage';
// import nvgtrLogo from "../../../assets/nvgtr_BM.svg";
// import nvgtrLogoWhite from "../../../assets/nvgtr_BM_white.svg";
import { MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../../features/theme/context/ThemeContext';
import UnifiedLoadingIndicator from './UnifiedLoadingIndicator';
// import { getBrandAssetsCached } from '../../../services/brandAssets';

const ChatMessages = ({
  messages,
  setMessages,
  chatId,
  chatTitle,
  typingMessage,
  isLoading,
  showGreeting,
  renderGreeting,
  handleCopy,
  handleEdit,
  handleReprompt,
  handleFeedbackSubmit,
  editingMessageIndex,
  setEditingMessageIndex,
  editInput,
  setEditInput,
  searchState,
  chatInputHeight = 130,
  projectData,
  currentUser,
  toolCallManager,
  // Pagination props
  onLoadMore,
  hasMoreMessages = false,
  isLoadingMore = false,
  chatContainerRef,
  onOpenArtifact
}) => {
  const messagesEndRef = useRef(null);
  const messageRefs = useRef({});
  const { darkMode } = useTheme();

  const onDraftSent = useCallback((messageId, { to, subject, sentAt }) => {
    if (!setMessages) return;
    setMessages(prev => prev.map(m => m._id === messageId ? {
      ...m,
      metadata: { ...(m.metadata || {}), sentDrafts: [...(m.metadata?.sentDrafts || []), { to, subject, sentAt }] }
    } : m));
  }, [setMessages]);

  const onUpdateMessageContent = useCallback((messageId, newText, responseIndex) => {
    if (!setMessages) return;
    setMessages(prev => prev.map(m => {
      if (m._id !== messageId) return m;
      if (m.responses && m.responses.length > 0 && typeof responseIndex === 'number') {
        const updated = [...m.responses];
        if (responseIndex >= 0 && responseIndex < updated.length) {
          updated[responseIndex] = { ...updated[responseIndex], text: newText };
          return { ...m, responses: updated };
        }
      }
      return { ...m, text: newText };
    }));
  }, [setMessages]);
  const [, setIsBotResponding] = useState(false);
  const [, setHasUserScrolled] = useState(false);
  const scrollPositionBeforeLoad = useRef(0);
  const scrollHeightBeforeLoad = useRef(0);

  /*
  // Add branding state (typing-row logo circle only; disabled with no bot avatar experiment)
  const [logoUrl, setLogoUrl] = useState(() => {
    try {
      const cachedLogoData = localStorage.getItem('logoCache');
      if (cachedLogoData) {
        const { logoUrl, isDark, hasCustomAssets } = JSON.parse(cachedLogoData);
        if (isDark === darkMode && hasCustomAssets && logoUrl && !logoUrl.includes('nvgtr_BM')) {
          return logoUrl;
        }
      }
    } catch (e) {
    }
    return darkMode ? nvgtrLogoWhite : nvgtrLogo;
  });
  const [logoLoaded, setLogoLoaded] = useState(false);

  const getProxiedImageUrl = (s3Url) => {
    if (!s3Url) return null;
    if (!s3Url.includes('amazonaws.com')) {
      return s3Url;
    }
    const urlParts = s3Url.split('/');
    const fileKey = urlParts.slice(3).join('/');
    return `${process.env.REACT_APP_API_URL}/brand/image-proxy?key=${encodeURIComponent(fileKey)}`;
  };

  useEffect(() => {
    const fetchBrandAssets = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLogoUrl(darkMode ? nvgtrLogoWhite : nvgtrLogo);
          setLogoLoaded(true);
          return;
        }
        const { brandAssets } = await getBrandAssetsCached({ token });
        if (brandAssets?.isProcessed && brandAssets?.isEnabled !== false) {
          const logo = darkMode ?
            brandAssets.brandMark?.white :
            brandAssets.brandMark?.black;
          if (logo) {
            const proxyUrl = getProxiedImageUrl(logo);
            setLogoUrl(proxyUrl);
            setLogoLoaded(true);
            try {
              localStorage.setItem('logoCache', JSON.stringify({
                logoUrl: proxyUrl,
                isDark: darkMode,
                hasCustomAssets: true,
                timestamp: Date.now()
              }));
            } catch (e) {
            }
            return;
          }
        }
        setLogoUrl(darkMode ? nvgtrLogoWhite : nvgtrLogo);
        setLogoLoaded(true);
        try {
          localStorage.setItem('logoCache', JSON.stringify({
            logoUrl: darkMode ? nvgtrLogoWhite : nvgtrLogo,
            isDark: darkMode,
            hasCustomAssets: false,
            timestamp: Date.now()
          }));
        } catch (e) {
        }
      } catch (error) {
        console.error('Error fetching brand assets:', error);
        setLogoUrl(darkMode ? nvgtrLogoWhite : nvgtrLogo);
        setLogoLoaded(true);
      }
    };
    fetchBrandAssets();
  }, [darkMode]);

  useEffect(() => {
    if (logoLoaded) {
      const currentLogo = darkMode ? nvgtrLogoWhite : nvgtrLogo;
      if (logoUrl === nvgtrLogo || logoUrl === nvgtrLogoWhite) {
        setLogoUrl(currentLogo);
      }
    }
  }, [darkMode, logoLoaded, logoUrl]);
  */

  // Intercept copy from user message bubbles to strip phantom trailing newlines
  useEffect(() => {
    const handleCopy = (e) => {
      const sel = window.getSelection();
      const text = sel?.toString();
      if (!text) return;

      const inUserMessage = (node) => {
        if (!node) return false;
        const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
        return el?.closest?.('.user-message');
      };
      if (inUserMessage(sel.anchorNode) || inUserMessage(sel.focusNode)) {
        e.preventDefault();
        e.clipboardData.setData('text/plain', text.replace(/\n+$/, ''));
      }
    };
    document.addEventListener('copy', handleCopy, true);
    return () => document.removeEventListener('copy', handleCopy, true);
  }, []);

  // Track when bot is responding (temporary message present)
  useEffect(() => {
    const tempBotMessage = messages.some(
      msg => msg && msg.sender === 'bot' && (msg.isTemporary || msg.isThinking)
    );
    setIsBotResponding(tempBotMessage);
    
    // Reset user scrolled flag when a conversation completes (no temp messages)
    if (!tempBotMessage && messages.length > 0) {
      setHasUserScrolled(false);
    }
  }, [messages]);

  // Scroll to active search result
  useEffect(() => {
    if (searchState?.query && searchState?.activeMessageIndex >= 0) {
      
      // Find the message at the active index
      const targetMessage = messages[searchState.activeMessageIndex];
      if (!targetMessage) {
        return;
      }
      
      // Get the ref key based on message ID
      const refKey = targetMessage._id || `${targetMessage.sender}-${searchState.activeMessageIndex}`;
      const messageEl = messageRefs.current[refKey];
      
      if (messageEl) {
        setTimeout(() => {
          messageEl.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center'
          });
          
          // Add a temporary highlight animation to make it clearer which message is active
          messageEl.classList.add('search-pulse-highlight');
          setTimeout(() => {
            messageEl.classList.remove('search-pulse-highlight');
          }, 1000);
        }, 50);
      }
    }
  }, [searchState?.activeMessageIndex, searchState?.query, searchState?.activeMatchIndex, messages]);

  // Simple indicator for total matches
  const renderSearchCountIndicator = () => {
    if (!searchState?.query || !searchState?.totalMatches) return null;
    
    return (
      <div 
        className="fixed right-4 z-20"
        style={{ bottom: `${chatInputHeight + 70}px` }}
      >
        <div className="bg-[#f5f5f5] dark:bg-[#282828] border border-[#d9d9d9] dark:border-[#3c3c3c] shadow-sm rounded-[12px] px-3 py-2 flex items-center gap-2">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          <span className="text-xs text-gray-700 dark:text-gray-300">
            {searchState.activeMatchIndex + 1} of {searchState.totalMatches} matches
          </span>
        </div>
      </div>
    );
  };

  const normalizeId = (value) => {
    if (!value) return null;
    if (typeof value === 'string' || typeof value === 'number') return value.toString();
    if (value._id) return value._id.toString();
    if (typeof value.toString === 'function') return value.toString();
    return null;
  };

  const getMessageDate = (message) => {
    if (!message) return null;
    const raw = message.timestamp || message.createdAt || message.updatedAt;
    if (!raw) return null;
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatTimestampLabel = (date) => {
    if (!date) return '';
    const now = new Date();
    const todayString = now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    let datePart;
    if (date.toDateString() === todayString) {
      datePart = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      datePart = 'Yesterday';
    } else {
      datePart = date.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    }

    const timePart = date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit'
    });

    return `${datePart} · ${timePart}`;
  };

  const shouldShowTimestamp = (currentPair, previousPair) => {
    if (!projectData?.id) return false;
    const currentDate = getMessageDate(currentPair?.userMessage) || getMessageDate(currentPair?.botMessage);
    if (!currentDate) return false;
    if (!previousPair) return true;

    const previousDate = getMessageDate(previousPair?.userMessage) || getMessageDate(previousPair?.botMessage);
    if (!previousDate) return true;

    const diffMinutes = Math.abs(currentDate - previousDate) / 60000;
    const differentDay = currentDate.toDateString() !== previousDate.toDateString();

    return differentDay || diffMinutes >= 45;
  };

  // Group messages into conversation pairs (user + bot response)
  const conversationPairs = React.useMemo(() => {
    const pairs = [];
    let currentPair = null;
    
    messages.forEach((message, index) => {
      if (message.sender === 'user') {
        // Start a new conversation pair - use the user message ID as the stable key
        // Using message._id instead of array index prevents React from
        // incorrectly reusing components during streaming when the messages array updates.
        // This works in conjunction with the fix in useMessageStreaming.js that prevents
        // temporary user messages from being filtered out during bot message creation.
        currentPair = {
          id: `pair-${message._id || `user-${index}`}`, // Use message ID for stable key
          userMessage: message,
          userIndex: index,
          botMessage: null,
          botIndex: -1,
          isComplete: false
        };
        pairs.push(currentPair);
      } else if (message.sender === 'bot' && currentPair) {
        // Add bot response to current pair
        currentPair.botMessage = message;
        currentPair.botIndex = index;
        currentPair.isComplete = !message.isStreaming && !message.isTemporary && !message.isThinking;
      }
    });
    
    return pairs;
  }, [messages]);

  // Dynamic height calculation based on user message length
  const calculateDynamicHeight = (userMessage) => {
    if (!userMessage) {
      return 'calc(100vh - 290px)'; // Default fallback
    }

    // Check different possible content properties
    const content = userMessage.content || userMessage.text || userMessage.message || '';
    
    if (!content) {
      return 'calc(100vh - 290px)'; // Default fallback
    }

    const lines = content.split('\n').length;
    const contentLength = content.length;
    
    // Base offset for single line messages
    let heightOffset = 220;
    
    // Adjustments for longer messages (subtle - avoid over-pushing)
    if (lines > 1) {
      // Reduce height offset by 8px per additional line
      heightOffset -= (lines - 1) * 8;
    }
    
    // Content-based adjustment
    if (contentLength > 65) {
      const extraLength = contentLength - 65;
      // 30px reduction per 100 characters
      const additionalReduction = Math.floor(extraLength / 100) * 30;
      heightOffset -= additionalReduction;
    }
    
    // For very long messages (850+ chars)
    if (contentLength > 850) {
      const longReduction = Math.floor((contentLength - 850) / 425) * 18;
      heightOffset -= longReduction;
    }
    
    // For massive messages (2100+ chars)
    if (contentLength > 2100) {
      const massiveReduction = Math.floor((contentLength - 2100) / 550) * 15;
      heightOffset -= massiveReduction;
    }
    
    // UNLIMITED - no minimum limit, let it go as negative as needed
    // This allows infinite scroll-up for any message length
    
    return `calc(100vh - ${heightOffset}px)`;
  };

  // Auto-scroll to bottom when user sends a message, then stay there
  useEffect(() => {
    if (conversationPairs.length > 0) {
      const lastPair = conversationPairs[conversationPairs.length - 1];
      
      // Check if the last pair has a new user message (just sent)
      if (lastPair.userMessage && !lastPair.botMessage) {
        // Simple: scroll to bottom and stay there
        setTimeout(() => {
          const anyPairElement = document.querySelector('[data-pair-id]');
          
          if (anyPairElement) {
            // Find the chat container by traversing up from the pair element
            let chatContainer = anyPairElement.parentElement; // This is the ChatMessages div
            while (chatContainer && !chatContainer.classList.contains('overflow-y-auto')) {
              chatContainer = chatContainer.parentElement;
            }
            
            if (chatContainer) {
              chatContainer.scrollTo({
                top: chatContainer.scrollHeight,
                behavior: 'smooth' // Smooth scroll to bottom
              });
            }
          }
        }, 100); // Small delay to ensure DOM is updated
      }
    }
  }, [conversationPairs]);

  // Track previous chatId to detect initial load vs. streaming completion
  const prevChatIdRef = useRef(null);

  // Auto-scroll to bottom on initial load (but not when dynamic height is in effect)
  // Only scroll when user is near bottom - prevents "zapping" when user has scrolled up to read
  useEffect(() => {
    if (conversationPairs.length > 0) {
      const lastPair = conversationPairs[conversationPairs.length - 1];
      
      // Don't auto-scroll to bottom if:
      // 1. The last pair has a user message without a bot response (initial positioning)
      // 2. The last pair is actively streaming (maintain position during streaming)
      if (lastPair && lastPair.userMessage && (!lastPair.botMessage || lastPair.botMessage?.isStreaming)) {
        return;
      }
      
      // Find the chat container
      const chatContainer = chatContainerRef?.current || (() => {
        const anyPairElement = document.querySelector('[data-pair-id]');
        if (!anyPairElement) return null;
        let container = anyPairElement.parentElement;
        while (container && !container.classList.contains('overflow-y-auto')) {
          container = container.parentElement;
        }
        return container;
      })();
      
      if (!chatContainer) return;
      
      // Only scroll if user is near the bottom (within 150px) - prevents zap when reading up
      const { scrollTop, scrollHeight, clientHeight } = chatContainer;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isNearBottom = distanceFromBottom <= 150;
      
      // On chat switch (chatId change) or initial load, always scroll to bottom. Otherwise only if user is near bottom.
      const isChatSwitch = prevChatIdRef.current !== chatId;
      prevChatIdRef.current = chatId;
      
      if (!isChatSwitch && !isNearBottom) {
        return; // User scrolled up to read - don't yank them back
      }
      
      chatContainer.scrollTo({
        top: chatContainer.scrollHeight,
        behavior: isChatSwitch ? 'instant' : 'smooth'
      });
    }
  }, [chatId, conversationPairs, chatContainerRef]); // Trigger when chatId changes (new chat loaded) OR when messages load

  // Scroll detection for loading more messages
  useEffect(() => {
    if (!chatContainerRef?.current || !onLoadMore || !hasMoreMessages) return;

    const container = chatContainerRef.current;
    let isLoadingMoreMessages = false;

    const handleScroll = () => {
      if (isLoadingMoreMessages || isLoadingMore) return;

      const scrollTop = container.scrollTop;
      const threshold = 100; // Load more when within 100px of top

      if (scrollTop <= threshold && hasMoreMessages) {
        console.log('📜 Reached top, loading more messages...');
        isLoadingMoreMessages = true;
        
        // Store current scroll position and height
        scrollPositionBeforeLoad.current = scrollTop;
        scrollHeightBeforeLoad.current = container.scrollHeight;
        
        onLoadMore();
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [chatContainerRef, onLoadMore, hasMoreMessages, isLoadingMore]);

  // Maintain scroll position after loading more messages
  useEffect(() => {
    if (!chatContainerRef?.current || !isLoadingMore) return;

    const container = chatContainerRef.current;
    
    // After loading completes, adjust scroll position to maintain user's view
    const observer = new MutationObserver(() => {
      const newScrollHeight = container.scrollHeight;
      const heightDifference = newScrollHeight - scrollHeightBeforeLoad.current;
      
      if (heightDifference > 0) {
        // Adjust scroll position to maintain the same visible content
        container.scrollTop = scrollPositionBeforeLoad.current + heightDifference;
        console.log('📜 Adjusted scroll position after loading more messages');
      }
    });

    observer.observe(container, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, [isLoadingMore, chatContainerRef]);

  return (
    <div className="flex-1">
      {/* Loading more indicator at top */}
      {isLoadingMore && (
        <div className="flex justify-center py-4">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading more messages...</span>
          </div>
        </div>
      )}
      
      {/* Show conversation pairs */}
      {conversationPairs.map((pair, pairIndex) => {
        const isLastPair = pairIndex === conversationPairs.length - 1;
        const previousPair = pairIndex > 0 ? conversationPairs[pairIndex - 1] : null;
        const nextPair = pairIndex < conversationPairs.length - 1 ? conversationPairs[pairIndex + 1] : null;
        const showTimestamp = shouldShowTimestamp(pair, previousPair);
        const timestampDate = getMessageDate(pair.userMessage) || getMessageDate(pair.botMessage);
        const userMessageOwnerId = normalizeId(pair.userMessage?.userId);
        const currentUserId = normalizeId(currentUser?.id);
        const isCurrentUserMessage = Boolean(userMessageOwnerId && currentUserId && userMessageOwnerId === currentUserId);
        const previousPairUserOwnerId = normalizeId(previousPair?.userMessage?.userId);
        const isConsecutiveFromSameUser = Boolean(
          previousPair &&
          userMessageOwnerId &&
          previousPairUserOwnerId &&
          userMessageOwnerId === previousPairUserOwnerId
        );
        const nextPairUserOwnerId = normalizeId(nextPair?.userMessage?.userId);
        const hasNextSameUser = Boolean(
          nextPair &&
          !pair.botMessage &&
          userMessageOwnerId &&
          nextPairUserOwnerId &&
          userMessageOwnerId === nextPairUserOwnerId
        );
        const baseTopSpacing = isCurrentUserMessage ? 'mt-4' : 'mt-2';
        const baseBottomSpacing = isCurrentUserMessage ? 'mb-6' : 'mb-3';
        const topSpacingClass = isConsecutiveFromSameUser ? 'mt-1' : baseTopSpacing;
        const bottomSpacingClass = hasNextSameUser ? 'mb-2' : baseBottomSpacing;
        const isUserMessageInSequence = isConsecutiveFromSameUser || hasNextSameUser;
        const botHasText = Boolean(pair.botMessage?.text && pair.botMessage.text.trim() !== '');
        const botHasImage = Boolean(pair.botMessage?.generatedImage?.presignedUrl);
        const botHasAttachments = Boolean(pair.botMessage?.attachments?.length);
        const botHasSearchState = Boolean(
          pair.botMessage?.isSearching ||
          pair.botMessage?.hasWebSearch ||
          pair.botMessage?.searchStatus ||
          (pair.botMessage?.searchSources && pair.botMessage.searchSources.length > 0) ||
          pair.botMessage?.hubspotFunction ||
          pair.botMessage?.hasHubSpotFunction
        );
        const botHasAnalysisState = Boolean(
          pair.botMessage?.isAnalyzing ||
          pair.botMessage?.analysisStatus ||
          pair.botMessage?.analysisProgress
        );
        const botHasDeepResearchState = Boolean(
          pair.botMessage?.isDeepResearch ||
          (pair.botMessage?.metadata?.stage && pair.botMessage?.metadata?.progress)
        );
        const botHasImageGenState = Boolean(
          pair.botMessage?.isGeneratingImage ||
          pair.botMessage?.isEditingImage ||
          pair.botMessage?.imageGenerationCanceled ||
          pair.botMessage?.imageEditCanceled ||
          pair.botMessage?.showImageSkeleton
        );
        const botHasDocumentState = Boolean(
          pair.botMessage?.isReadingDocument ||
          pair.botMessage?.documentAnalysisCanceled ||
          pair.botMessage?.documentCount
        );
        const botHasSuperbucketState = Boolean(
          pair.botMessage?.isQueryingSuperbucket ||
          pair.botMessage?.showSuperbucketSkeleton
        );
        const botHasThinkingState = Boolean(
          pair.botMessage?.isClaudeThinking ||
          pair.botMessage?.thinkingContent ||
          pair.botMessage?.thinkingDuration
        );
        const botHasToolCallState = Boolean(
          pair.botMessage?.toolCallsActive ||
          pair.botMessage?.hasToolCalls ||
          (pair.botMessage?.toolCallPositions && pair.botMessage.toolCallPositions.length > 0)
        );
        const botHasRenderableContent = Boolean(
          pair.botMessage && (
            botHasText ||
            botHasImage ||
            botHasAttachments ||
            botHasSearchState ||
            botHasAnalysisState ||
            botHasDeepResearchState ||
            botHasImageGenState ||
            botHasDocumentState ||
            botHasSuperbucketState ||
            botHasThinkingState ||
            botHasToolCallState
          )
        );
        
        return (
          <div 
            key={pair.id}
            data-pair-id={pair.id}
            className="flex flex-col px-4"
            style={isLastPair ? { minHeight: calculateDynamicHeight(pair.userMessage) } : undefined}
          >
            <div className="max-w-[800px] mx-auto w-full flex-1 flex flex-col max-[1200px]:max-w-[760px] max-[900px]:max-w-[700px] [@media(max-height:800px)]:max-w-[740px] [@media(max-height:700px)]:max-w-[680px]">
              {showTimestamp && timestampDate && (
                <div className="flex justify-center mb-6">
                  <div className="px-3 py-1 text-xs rounded-full bg-[#efefef] dark:bg-[#1f1f1f] text-gray-600 dark:text-gray-300">
                    {formatTimestampLabel(timestampDate)}
                  </div>
                </div>
              )}
              {/* User message positioned at top-right */}
              {pair.userMessage && (
                <div
                  className={`flex ${isCurrentUserMessage ? 'justify-end' : 'justify-start'} ${topSpacingClass} ${bottomSpacingClass}`}
                >
                  <div 
                    className="max-w-[75%]"
                    ref={el => messageRefs.current[pair.userMessage._id || `user-${pair.userIndex}`] = el}
                  >
                    {/* Check if this is the active search message */}
                    <div className={`${
                      searchState?.query && searchState?.activeMessageIndex === pair.userIndex
                        ? 'bg-yellow-50/80 dark:bg-yellow-900/30 -mx-4 px-4 py-2 rounded-lg ring-2 ring-yellow-300 dark:ring-yellow-700'
                        : ''
                    } transition-all duration-200`}>
                      <ChatMessage
                        message={pair.userMessage}
                        chatId={chatId}
                        chatTitle={chatTitle}
                        handleCopy={handleCopy}
                        handleEdit={handleEdit}
                        handleReprompt={handleReprompt}
                        handleFeedbackSubmit={handleFeedbackSubmit}
                        editingMessageIndex={editingMessageIndex}
                        setEditingMessageIndex={setEditingMessageIndex}
                        editInput={editInput}
                        setEditInput={setEditInput}
                        searchQuery={searchState?.query}
                        isActiveSearchResult={searchState?.query && searchState?.activeMessageIndex === pair.userIndex}
                        messages={messages}
                        projectData={projectData}
                        currentUser={currentUser}
                        toolCallManager={toolCallManager}
                        isOwnMessage={isCurrentUserMessage}
                        hideUserHeader={isConsecutiveFromSameUser}
                        compactUserMessage={isUserMessageInSequence}
                        onOpenArtifact={onOpenArtifact}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Bot response positioned below with room to expand */}
              {pair.botMessage && botHasRenderableContent && (
                <div className="flex justify-start flex-1">
                  <div 
                    className="w-full"
                    ref={el => messageRefs.current[pair.botMessage._id || `bot-${pair.botIndex}`] = el}
                  >
                    <div className={`${
                      searchState?.query && searchState?.activeMessageIndex === pair.botIndex
                        ? 'bg-yellow-50/80 dark:bg-yellow-900/30 -mx-4 px-4 py-2 rounded-lg ring-2 ring-yellow-300 dark:ring-yellow-700'
                        : ''
                    } transition-all duration-200`}>
                      <ChatMessage
                        message={pair.botMessage}
                        chatId={chatId}
                        chatTitle={chatTitle}
                        handleCopy={handleCopy}
                        handleEdit={handleEdit}
                        handleReprompt={handleReprompt}
                        handleFeedbackSubmit={handleFeedbackSubmit}
                        editingMessageIndex={editingMessageIndex}
                        setEditingMessageIndex={setEditingMessageIndex}
                        editInput={editInput}
                        setEditInput={setEditInput}
                        searchQuery={searchState?.query}
                        isActiveSearchResult={searchState?.query && searchState?.activeMessageIndex === pair.botIndex}
                        messages={messages}
                        projectData={projectData}
                        currentUser={currentUser}
                        toolCallManager={toolCallManager}
                        onDraftSent={setMessages ? onDraftSent : undefined}
                        onUpdateMessageContent={setMessages ? onUpdateMessageContent : undefined}
                        onOpenArtifact={onOpenArtifact}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Show typing indicator ONLY until bot content is available */}
              {pair.userMessage && 
               !pair.userMessage?.isDiscussionOnly && 
               !botHasRenderableContent && (
                <div className="flex justify-start">
                    <div className="flex items-start w-full -mb-4">
                    {/*
                    <div className="chat-logo-avatar flex-shrink-0 pr-3 hidden sm:block">
                      <div className="w-[26px] h-[26px] rounded-full bg-[#F9FAFB] dark:bg-[#282828] border border-[#E5E7EB] dark:border-[#444444] flex items-center justify-center max-[1200px]:w-[22px] max-[1200px]:h-[22px] max-[900px]:w-[20px] max-[900px]:h-[20px] [@media(max-height:800px)]:w-[22px] [@media(max-height:800px)]:h-[22px] [@media(max-height:700px)]:w-[20px] [@media(max-height:700px)]:h-[20px]">
                        <img src={logoUrl} alt="Midus" className="w-[18px] h-[18px] max-[1200px]:w-[15px] max-[1200px]:h-[15px] max-[900px]:w-[14px] max-[900px]:h-[14px] [@media(max-height:800px)]:w-[15px] [@media(max-height:800px)]:h-[15px] [@media(max-height:700px)]:w-[14px] [@media(max-height:700px)]:h-[14px]" />
                      </div>
                    </div>
                    */}
                    {/* was: pl-2 sm:pl-0 on flex-1 (pl-2 commented out with no logo circle experiment) */}
                    <div className="flex-1 whitespace-pre-wrap flex items-center">
                      <UnifiedLoadingIndicator indicatorConfig={null} fallbackToDots={true} darkMode={darkMode} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Search results indicator */}
      {renderSearchCountIndicator()}

      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessages;